import { useEffect, useId, useState, type ChangeEvent, type ClipboardEvent, type DragEvent, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { Modal } from '../common/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import {
  CATEGORY_OPTIONS,
  FeedbackError,
  MAX_DESCRIPTION_LEN,
  MAX_FILES,
  MAX_FILE_BYTES,
  MAX_LOG_PASTE_LEN,
  MAX_TITLE_LEN,
  isAcceptedFileType,
  moduleFromPath,
  submitFeedback,
  type FeedbackCategory,
} from '../../services/feedback';

/**
 * Feedback dialog (#120), matching Desk's: category, title, description, screenshots (picker, paste, drag and drop),
 * and for bugs the logs. Reports go to QA's intake, which files the GitHub issue.
 */

interface FormState {
  category: FeedbackCategory;
  title: string;
  description: string;
  attachActivity: boolean;
  manualConsoleLogs: string;
  manualNetworkLogs: string;
  files: File[];
  /** Bug only: must confirm this is a genuine bug, not a suggestion. Reset when the category changes. */
  bugAcknowledged: boolean;
}

// 'question' is the default: a pre-selected "bug" mis-filed too many suggestions as bugs (Desk's finding).
const INITIAL_FORM: FormState = {
  category: 'question',
  title: '',
  description: '',
  attachActivity: false,
  manualConsoleLogs: '',
  manualNetworkLogs: '',
  files: [],
  bugAcknowledged: false,
};

const MAX_MB = MAX_FILE_BYTES / (1024 * 1024);

/** A small preview for an image attachment (the object URL is released when the row goes away). */
function Thumbnail({ file }: { file: File }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!file.type.startsWith('image/') || typeof URL.createObjectURL !== 'function') return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => {
      if (typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
    };
  }, [file]);
  if (!src) return null;
  return <img className="feedback-thumb" src={src} alt="" width={40} height={40} />;
}

export function FeedbackModal({ onClose }: { onClose: () => void }) {
  const location = useLocation();
  const { user } = useAuth();
  const { tenantId, tenant } = useTenant();
  const ids = {
    category: useId(),
    title: useId(),
    description: useId(),
    console: useId(),
    network: useId(),
    files: useId(),
    ack: useId(),
    error: useId(),
  };

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ issueNumber: number; issueUrl: string } | null>(null);
  const [dragging, setDragging] = useState(false);

  const isBug = form.category === 'bug';
  const title = form.title.trim();
  const description = form.description.trim();
  const valid =
    title.length > 0 &&
    title.length <= MAX_TITLE_LEN &&
    description.length > 0 &&
    description.length <= MAX_DESCRIPTION_LEN &&
    (!isBug || form.bugAcknowledged);

  /** Add files from the picker or a drop, with the same checks and messages as Desk. */
  function addPicked(picked: File[]) {
    if (picked.length === 0) return;
    const wrongType = picked.find((f) => !isAcceptedFileType(f.type));
    if (wrongType) {
      setError(
        `"${wrongType.name}" isn't a supported attachment type. Allowed: images, PDF, text/JSON/CSV, ZIP, and Office documents (SVG excluded).`
      );
      return;
    }
    const oversized = picked.find((f) => f.size > MAX_FILE_BYTES);
    if (oversized) {
      setError(`"${oversized.name}" exceeds the ${MAX_MB} MB limit.`);
      return;
    }
    append(picked);
  }

  /** Add checked files up to the limit, and say which ones did not fit instead of dropping them silently. */
  function append(files: File[]) {
    const room = Math.max(0, MAX_FILES - form.files.length);
    setForm((s) => ({ ...s, files: [...s.files, ...files].slice(0, MAX_FILES) }));
    const ignored = files.slice(room);
    setError(
      ignored.length > 0
        ? `Only ${MAX_FILES} attachments can be sent. Not added: ${ignored.map((f) => f.name).join(', ')}.`
        : null
    );
  }

  function handleFilesChange(e: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = ''; // so the same file can be chosen again after removing it
    addPicked(picked);
  }

  function handlePaste(e: ClipboardEvent<HTMLFormElement>) {
    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;
    const pasted: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind !== 'file' || !isAcceptedFileType(item.type)) continue;
      const blob = item.getAsFile();
      if (!blob) continue;
      const ext = (item.type.split('/')[1] || 'png').replace(/[^a-z0-9]/gi, '');
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      pasted.push(new File([blob], `pasted-${ts}.${ext}`, { type: item.type }));
    }
    if (pasted.length === 0) return; // text pastes into the fields carry on as normal
    e.preventDefault();
    if (pasted.some((f) => f.size > MAX_FILE_BYTES)) {
      setError(`Pasted image exceeds the ${MAX_MB} MB limit.`);
      return;
    }
    append(pasted);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    if (submitting) return;
    addPicked(Array.from(e.dataTransfer?.files ?? []));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!valid || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await submitFeedback({
        category: form.category,
        title,
        description,
        module: moduleFromPath(location.pathname),
        attachActivity: isBug && form.attachActivity,
        manualConsoleLogs: isBug ? form.manualConsoleLogs : undefined,
        manualNetworkLogs: isBug ? form.manualNetworkLogs : undefined,
        files: form.files,
        context: { tenantId, tenantName: tenant?.name, role: tenant?.role, route: location.pathname },
        reportedBy:
          user?.displayName && user.email ? `${user.displayName} <${user.email}>` : (user?.email ?? user?.displayName ?? ''),
      });
      setSuccess(result);
    } catch (err) {
      setError(err instanceof FeedbackError || err instanceof Error ? err.message : 'Unknown error sending feedback.');
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <Modal title="Thanks for the feedback" onClose={onClose}>
        <p>Your report has been filed and the team will take a look.</p>
        <p>
          We logged this as issue{' '}
          <a href={success.issueUrl} target="_blank" rel="noopener noreferrer">
            #{success.issueNumber}
          </a>{' '}
          in the Detect repo.
        </p>
        <div className="modal-footer feedback-footer">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setForm(INITIAL_FORM);
              setError(null);
              setSuccess(null);
            }}
          >
            Send another
          </button>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Send feedback" onClose={onClose} wide locked={submitting} dismissOnBackdrop={false}>
      <form onSubmit={(e) => void handleSubmit(e)} onPaste={handlePaste} noValidate className="feedback-form">
        <p className="text-muted">Report a bug, request a feature, or send a comment to the Detect team.</p>

        {error && (
          <div id={ids.error} role="alert" className="error-state">
            {error}
          </div>
        )}

        <div className="form-field">
          <label htmlFor={ids.category}>Category</label>
          <select
            id={ids.category}
            value={form.category}
            disabled={submitting}
            onChange={(e) => {
              const next = e.target.value as FeedbackCategory;
              // Re-picking bug later must ask for a fresh acknowledgement.
              setForm((s) => ({ ...s, category: next, bugAcknowledged: next === 'bug' ? s.bugAcknowledged : false }));
            }}
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {isBug && (
          <div role="alert" className="feedback-bug-notice">
            <p>
              Bugs are only for something that isn&apos;t working as intended — not a new feature, an enhancement idea,
              or a &quot;it would be nice if…&quot; suggestion. If you&apos;re not sure, please select{' '}
              <strong>Question</strong> or <strong>Other feedback</strong> above instead.
            </p>
            <label htmlFor={ids.ack} className="feedback-check">
              <input
                id={ids.ack}
                type="checkbox"
                checked={form.bugAcknowledged}
                disabled={submitting}
                onChange={(e) => setForm((s) => ({ ...s, bugAcknowledged: e.target.checked }))}
              />
              <span>I confirm this is a genuine bug, not a feature request or suggestion.</span>
            </label>
          </div>
        )}

        <div className="form-field">
          <label htmlFor={ids.title}>Title</label>
          <input
            id={ids.title}
            type="text"
            required
            autoFocus
            maxLength={MAX_TITLE_LEN}
            value={form.title}
            disabled={submitting}
            placeholder="Short summary of what happened"
            onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
          />
          <span className="text-muted feedback-count">
            {form.title.length}/{MAX_TITLE_LEN}
          </span>
        </div>

        <div className="form-field">
          <label htmlFor={ids.description}>Description</label>
          <textarea
            id={ids.description}
            required
            rows={6}
            maxLength={MAX_DESCRIPTION_LEN}
            value={form.description}
            disabled={submitting}
            placeholder={
              isBug
                ? 'What happened? What were you trying to do? Steps to reproduce, if any.'
                : 'Share your feedback.'
            }
            onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
          />
          <span className="text-muted feedback-count">
            {form.description.length}/{MAX_DESCRIPTION_LEN}
          </span>
        </div>

        <div className="form-field">
          <label htmlFor={ids.files}>
            Screenshots / attachments{' '}
            <span className="text-muted">
              (optional, up to {MAX_FILES} × {MAX_MB} MB — or paste an image with{' '}
              <kbd>{typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform) ? '⌘V' : 'Ctrl+V'}</kbd>)
            </span>
          </label>
          <div
            className={dragging ? 'feedback-drop feedback-drop-active' : 'feedback-drop'}
            data-testid="feedback-drop"
            onDragOver={(e) => {
              e.preventDefault();
              if (!submitting) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
          >
            <input
              id={ids.files}
              type="file"
              multiple
              accept="image/*,application/pdf,application/json,text/*,.csv,.zip,.doc,.docx,.xls,.xlsx"
              disabled={submitting || form.files.length >= MAX_FILES}
              onChange={handleFilesChange}
            />
            <span className="text-muted">or drop files here</span>
          </div>
          <p className="text-muted feedback-note">
            Screenshots are shared with the Meshtek team on GitHub. Don&apos;t include customer data or secrets.
          </p>
          {form.files.length > 0 && (
            <ul className="feedback-files">
              {form.files.map((f, i) => (
                <li key={`${f.name}-${i}`}>
                  <Thumbnail file={f} />
                  <span className="feedback-file-name">
                    {f.name} <span className="text-muted">({Math.round(f.size / 1024)} KB)</span>
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    aria-label={`Remove ${f.name}`}
                    disabled={submitting}
                    onClick={() => setForm((s) => ({ ...s, files: s.files.filter((_, j) => j !== i) }))}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {isBug && (
          <>
            <div className="form-field">
              <label htmlFor={ids.console}>
                Console logs <span className="text-muted">(optional)</span>
              </label>
              <textarea
                id={ids.console}
                rows={4}
                maxLength={MAX_LOG_PASTE_LEN}
                className="feedback-mono"
                value={form.manualConsoleLogs}
                disabled={submitting}
                placeholder="Paste relevant browser console output here (errors, warnings, etc.)"
                onChange={(e) => setForm((s) => ({ ...s, manualConsoleLogs: e.target.value }))}
              />
            </div>
            <div className="form-field">
              <label htmlFor={ids.network}>
                Network trace <span className="text-muted">(optional)</span>
              </label>
              <textarea
                id={ids.network}
                rows={4}
                maxLength={MAX_LOG_PASTE_LEN}
                className="feedback-mono"
                value={form.manualNetworkLogs}
                disabled={submitting}
                placeholder="Paste failing requests, status codes, response bodies, etc."
                onChange={(e) => setForm((s) => ({ ...s, manualNetworkLogs: e.target.value }))}
              />
            </div>
            <label className="feedback-check">
              <input
                type="checkbox"
                checked={form.attachActivity}
                disabled={submitting}
                onChange={(e) => setForm((s) => ({ ...s, attachActivity: e.target.checked }))}
              />
              <span>
                <strong>Auto-attach recent activity</strong>
                <span className="text-muted feedback-note">
                  Includes the last ~200 console messages and API request entries from this session. Merged with
                  anything you paste above. Uncheck for reports where the captured traffic may contain content you
                  don&apos;t want to share.
                </span>
              </span>
            </label>
          </>
        )}

        <div className="modal-footer feedback-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={!valid || submitting}>
            {submitting ? 'Sending…' : 'Send feedback'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
